import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import {
  FiBatteryCharging,
  FiCheckCircle,
  FiHeart,
  FiMinus,
  FiMic,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiShoppingCart,
  FiStar,
  FiTruck
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { db } from '../utils/firebase';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useHeroReveal, useScrollReveal } from '../hooks/useGsapMotion';

const formatCurrency = (value) => `BDT ${Number(value || 0).toLocaleString()}`;

const fallbackPalette = ['#111111', '#DADCE1', '#1C2945'];

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedColor, setSelectedColor] = useState('');

  const pageRef = useRef(null);

  useHeroReveal(pageRef, [product, loading]);
  useScrollReveal(pageRef, [product, loading, activeTab]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        const productDoc = await getDoc(doc(db, 'products', id));
        if (!productDoc.exists()) {
          toast.error('Product not found');
          navigate('/products');
          return;
        }

        const nextProduct = { id: productDoc.id, ...productDoc.data() };
        setProduct(nextProduct);
        setSelectedImage(nextProduct.image || '');
        setSelectedColor(nextProduct.colors?.[0] || fallbackPalette[0]);

        try {
          const relatedQuery = query(
            collection(db, 'products'),
            where('category', '==', nextProduct.category || ''),
            limit(8)
          );
          const relatedSnapshot = await getDocs(relatedQuery);
          const related = relatedSnapshot.docs
            .map((item) => ({ id: item.id, ...item.data() }))
            .filter((item) => item.id !== nextProduct.id)
            .slice(0, 3);

          setRelatedProducts(related);
        } catch (relatedError) {
          console.error('Error fetching related products:', relatedError);
          setRelatedProducts([]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Error loading product details');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  const allImages = useMemo(() => {
    const items = [product?.image, ...(product?.secondaryImages || [])].filter(Boolean);
    return [...new Set(items)];
  }, [product]);

  const rating = Number(product?.rating || 4.8);
  const stock = Number(product?.stock || 0);
  const discountPercentage = product?.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const colorPalette = product?.colors?.length ? product.colors : fallbackPalette;

  const specificationRows = [
    ['SKU', product?.id?.slice(0, 8).toUpperCase() || 'N/A'],
    ['Category', (product?.category || 'General').replace(/-/g, ' ')],
    ['Stock', stock > 0 ? `${stock} available` : 'Out of stock'],
    ['Warranty', '1 Year Official Warranty'],
    ['Delivery', 'Within 24 hours in Dhaka'],
    ['Returns', '7 Day Return Policy']
  ];

  const handleAddToCart = (targetQty = quantity) => {
    if (!product || stock <= 0) return;

    addToCart({
      ...product,
      quantity: targetQty
    });

    toast.success(`${targetQty} item(s) added to cart`);
  };

  if (loading) return <LoadingSpinner />;

  if (!product) {
    return (
      <div className="sc-container py-14 text-center">
        <h2 className="text-3xl font-black text-[#1A1A1A]">Product not found</h2>
        <button onClick={() => navigate('/products')} className="sc-btn-primary mt-5">
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="sc-container pb-20 pt-6 md:pb-24 md:pt-9">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-slate-500" data-hero-item>
        <button onClick={() => navigate('/')} className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </button>
        <span>/</span>
        <button
          onClick={() => navigate(`/category/${product.category}`)}
          className="sc-hover-smooth capitalize hover:text-[#DB4444]"
        >
          {product.category?.replace(/-/g, ' ') || 'Products'}
        </button>
        <span>/</span>
        <span className="font-semibold text-slate-700">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
        <section className="lg:col-span-7" data-hero-item>
          <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white">
            <span className="absolute left-4 top-4 z-10 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-700 shadow-sm backdrop-blur">
              Premium Choice
            </span>

            <button
              onClick={() => toggleWishlist(product)}
              className="sc-hover-smooth absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-slate-500 shadow-sm hover:scale-105 hover:text-[#DB4444]"
            >
              <FiHeart size={16} className={isInWishlist(product.id) ? 'fill-current text-[#DB4444]' : ''} />
            </button>

            <img
              src={selectedImage || '/placeholder-product.jpg'}
              alt={product.name}
              className="sc-hover-smooth h-[380px] w-full object-cover group-hover:scale-[1.03] sm:h-[470px]"
            />
          </div>

          {allImages.length > 1 && (
            <div className="hide-scrollbar mt-4 flex gap-4 overflow-x-auto pb-2">
              {allImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(image)}
                  className={`sc-hover-smooth h-20 w-20 shrink-0 overflow-hidden rounded-lg border sm:h-24 sm:w-24 ${
                    selectedImage === image
                      ? 'border-[#DB4444] ring-2 ring-[#DB4444]/20'
                      : 'border-slate-200 opacity-80 hover:scale-[1.03] hover:opacity-100'
                  }`}
                >
                  <img src={image} alt={`${product.name} ${index + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="lg:col-span-5">
          <div className="sc-soft-card top-24 p-6 lg:sticky" data-hero-item>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  {stock > 0 ? 'In Stock' : 'Out of Stock'}
                </span>
                <span className="text-xs font-medium text-slate-500">SKU: {product.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex items-center gap-1">
                <FiStar className="fill-current text-amber-400" size={15} />
                <span className="text-sm font-bold text-[#1A1A1A]">{rating.toFixed(1)}</span>
                <span className="sc-hover-smooth cursor-pointer text-sm text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-[#DB4444]">
                  ({product.reviews || 0} Reviews)
                </span>
              </div>
            </div>

            <h1 className="font-serif text-3xl font-bold leading-tight text-[#1A1A1A] md:text-4xl">
              {product.name}
            </h1>

            <div className="mt-4 flex items-baseline gap-3">
              <p className="text-3xl font-black text-[#DB4444]">{formatCurrency(product.price)}</p>
              {product.originalPrice && (
                <>
                  <p className="text-lg text-slate-400 line-through">{formatCurrency(product.originalPrice)}</p>
                  <span className="rounded bg-red-50 px-2 py-1 text-xs font-bold text-[#DB4444]">-{discountPercentage}%</span>
                </>
              )}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-slate-600">
              {product.description ||
                'A premium product engineered for all-day comfort, powerful performance and everyday reliability.'}
            </p>

            <div className="mt-6">
              <p className="mb-3 text-sm font-semibold text-[#1A1A1A]">
                Color: <span className="font-normal text-slate-500">{selectedColor}</span>
              </p>
              <div className="flex gap-3">
                {colorPalette.map((color, index) => (
                  <button
                    key={`${color}-${index}`}
                    onClick={() => setSelectedColor(color)}
                    className={`sc-hover-smooth h-9 w-9 rounded-full border ring-offset-2 hover:scale-105 ${
                      selectedColor === color
                        ? 'border-[#DB4444] ring-2 ring-[#DB4444]/40'
                        : 'border-slate-200 hover:ring-2 hover:ring-slate-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-end gap-4">
              <div className="w-36">
                <p className="mb-2 text-sm font-semibold text-[#1A1A1A]">Quantity</p>
                <div className="flex h-12 items-center rounded-lg border border-slate-200 bg-white">
                  <button
                    className="sc-hover-smooth flex h-full w-10 items-center justify-center text-slate-500 hover:text-[#1A1A1A]"
                    onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                    disabled={quantity <= 1}
                  >
                    <FiMinus size={14} />
                  </button>
                  <input
                    className="h-full w-full border-none bg-transparent p-0 text-center text-sm font-medium text-[#1A1A1A] focus:ring-0"
                    readOnly
                    value={quantity}
                  />
                  <button
                    className="sc-hover-smooth flex h-full w-10 items-center justify-center text-slate-500 hover:text-[#1A1A1A]"
                    onClick={() => setQuantity((prev) => Math.min(stock || 99, prev + 1))}
                    disabled={stock > 0 && quantity >= stock}
                  >
                    <FiPlus size={14} />
                  </button>
                </div>
              </div>

              <button
                className="sc-hover-smooth flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-[#DB4444] px-4 font-semibold text-white shadow-md shadow-[#DB4444]/30 hover:scale-[1.01] hover:bg-[#c93e3e] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => handleAddToCart(quantity)}
                disabled={stock <= 0}
              >
                <FiShoppingCart size={16} />
                Add to Cart
              </button>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-6">
              <InfoPill icon={<FiShield size={16} />} title="Official Warranty" subtitle="1 Year Brand Warranty" />
              <InfoPill icon={<FiTruck size={16} />} title="Fast Delivery" subtitle="In 24h within Dhaka" />
              <InfoPill icon={<FiRefreshCw size={16} />} title="7 Day Returns" subtitle="No questions asked" />
              <InfoPill icon={<FiCheckCircle size={16} />} title="Cash on Delivery" subtitle="Available nationwide" />
            </div>
          </div>
        </section>
      </div>

      <section className="mt-16 lg:w-[70%]" data-reveal>
        <div className="mb-7 border-b border-slate-200">
          <nav className="flex gap-7 text-sm">
            {['description', 'specifications', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`sc-hover-smooth border-b-2 px-1 py-4 font-semibold capitalize ${
                  activeTab === tab
                    ? 'border-[#DB4444] text-[#DB4444]'
                    : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {tab}
                {tab === 'reviews' ? ` (${product.reviews || 0})` : ''}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'description' && (
          <div className="space-y-5 text-slate-600">
            <h3 className="text-xl font-bold text-[#1A1A1A]">Industry-leading performance</h3>
            <p className="leading-relaxed">
              {product.description ||
                'Built with premium materials and engineered for lasting comfort, this product is ideal for everyday listening, work and travel.'}
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              <FeatureCard
                icon={<FiBatteryCharging size={16} />}
                title="Extended Battery"
                text="Optimized power management helps you stay unplugged longer without sacrificing quality."
              />
              <FeatureCard
                icon={<FiMic size={16} />}
                title="Clear Voice Pickup"
                text="Advanced tuning and directional pickup deliver clean calls and crisp voice clarity."
              />
            </div>
          </div>
        )}

        {activeTab === 'specifications' && (
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {specificationRows.map(([label, value]) => (
                <div key={label} className="rounded-lg bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="mt-1 text-sm font-medium text-[#1A1A1A] capitalize">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {[1, 2].map((index) => (
              <div key={index} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-bold text-[#1A1A1A]">Verified Buyer {index}</p>
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(5)].map((_, star) => (
                      <FiStar key={star} size={14} className="fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Smooth experience overall. Build quality feels premium and delivery was quick.
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {relatedProducts.length > 0 && (
        <section className="mt-16 border-t border-slate-200 pt-12" data-stagger>
          <h2 className="mb-6 text-2xl font-bold text-[#1A1A1A]">Frequently Bought Together</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {relatedProducts.map((item) => (
              <article
                key={item.id}
                className="group sc-hover-smooth overflow-hidden rounded-xl border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-lg"
                data-stagger-item
              >
                <div className="relative aspect-square overflow-hidden bg-slate-50">
                  <img
                    src={item.image || '/placeholder-product.jpg'}
                    alt={item.name}
                    className="h-full w-full object-cover sc-hover-smooth group-hover:scale-[1.04]"
                  />
                  <button
                    onClick={() => {
                      addToCart({ ...item, quantity: 1 });
                      toast.success('Item added to cart');
                    }}
                    className="sc-hover-smooth absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-500 shadow-sm hover:scale-105 hover:text-[#DB4444]"
                  >
                    <FiPlus size={15} />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="truncate text-sm font-semibold text-[#1A1A1A]">{item.name}</h3>
                  <p className="mt-1 text-base font-bold text-[#DB4444]">{formatCurrency(item.price)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white p-4 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-slate-500">Total Price</p>
            <p className="text-lg font-bold text-[#1A1A1A]">{formatCurrency(Number(product.price || 0) * quantity)}</p>
          </div>
          <button
            className="sc-hover-smooth h-11 flex-1 rounded-lg bg-[#DB4444] text-sm font-bold text-white hover:scale-[1.01] hover:bg-[#c93e3e]"
            onClick={() => handleAddToCart(quantity)}
            disabled={stock <= 0}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

const InfoPill = ({ icon, title, subtitle }) => (
  <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
    <div className="rounded-lg bg-white p-2 text-slate-600">{icon}</div>
    <div>
      <h4 className="text-sm font-semibold text-[#1A1A1A]">{title}</h4>
      <p className="text-xs text-slate-500">{subtitle}</p>
    </div>
  </div>
);

const FeatureCard = ({ icon, title, text }) => (
  <article className="rounded-xl border border-slate-200 bg-white p-5">
    <span className="mb-4 inline-flex rounded-full bg-[#DB4444]/10 p-2 text-[#DB4444]">{icon}</span>
    <h4 className="mb-2 text-sm font-bold text-[#1A1A1A]">{title}</h4>
    <p className="text-sm text-slate-600">{text}</p>
  </article>
);

export default ProductDetail;
